/**
 * BuyerProfileWizard — Buyer Readiness multi-step editor.
 *
 * Steps: Financials → Property Preferences → Locations → Review.
 * Persists to BuyerProfile + BuyerPreferences via the agents.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useBuyerProfile, useBuyerPreferences } from '../../agents/buyerProfileAgent';
import { Stepper, SectionCard, FieldError } from '../ui';
import { findRangeErrors, hasRangeErrors } from '../../utils/rangeValidation';
import './BuyerProfileWizard.css';

const STEPS = [
  { key: 'financials', label: 'Financials' },
  { key: 'preferences', label: 'Property Preferences' },
  { key: 'locations', label: 'Locations' },
  { key: 'review', label: 'Review' },
];

const FINANCING_OPTIONS = [
  { value: 'conventional', label: 'Conventional' },
  { value: 'fha', label: 'FHA' },
  { value: 'va', label: 'VA' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

const CREDIT_OPTIONS = [
  { value: 'excellent', label: 'Excellent (740+)' },
  { value: 'good', label: 'Good (670-739)' },
  { value: 'fair', label: 'Fair (580-669)' },
  { value: 'poor', label: 'Poor (<580)' },
  { value: 'unsure', label: 'Not sure' },
];

const EMPLOYMENT_OPTIONS = [
  { value: 'w2', label: 'W-2 Employee' },
  { value: 'self_employed', label: 'Self-employed' },
  { value: 'retired', label: 'Retired' },
  { value: 'other', label: 'Other' },
];

const PROPERTY_TYPES = ['House', 'Condo', 'Townhouse', 'Multi-family', 'Land'];
const COMMON_MUST_HAVES = ['Garage', 'Yard', 'Updated kitchen', 'Move-in ready', 'Home office'];
const COMMON_NICE_TO_HAVES = ['Pool', 'View', 'Fireplace', 'Smart home', 'Solar'];

const EMPTY_FORM = {
  budget_ceiling: '',
  pre_approval_amount: '',
  down_payment_percent: 20,
  max_monthly_payment: '',
  timeline_months: 6,
  financing_type: '',
  credit_score_range: '',
  employment_status: '',
  first_time_buyer: null,
  co_buyer_name: '',
  min_bedrooms: '',
  max_bedrooms: '',
  min_bathrooms: '',
  max_bathrooms: '',
  property_types: [],
  must_haves: [],
  nice_to_haves: [],
  max_hoa_fee: '',
  preferred_locations: [],
};

const PREFERENCES_STEP = STEPS.findIndex((s) => s.key === 'preferences');

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

const BuyerProfileWizard = ({ buyerId, initialStep = 0, onComplete, onCancel }) => {
  const [stepIdx, setStepIdx] = useState(initialStep);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [locationDraft, setLocationDraft] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    profile,
    fetchProfile,
    createProfile,
    updateProfile,
  } = useBuyerProfile(buyerId);

  const {
    preferences,
    fetchPreferences,
    createPreferences,
    updatePreferences,
  } = useBuyerPreferences(buyerId);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);
  useEffect(() => { fetchPreferences(); }, [fetchPreferences]);

  useEffect(() => {
    if (!profile) return;
    setFormData((prev) => ({
      ...prev,
      budget_ceiling: profile.budget_ceiling ?? '',
      pre_approval_amount: profile.pre_approval_amount ?? '',
      down_payment_percent: profile.down_payment_percent ?? 20,
      max_monthly_payment: profile.max_monthly_payment ?? '',
      timeline_months: profile.timeline_months ?? 6,
      financing_type: profile.financing_type ?? '',
      credit_score_range: profile.credit_score_range ?? '',
      employment_status: profile.employment_status ?? '',
      first_time_buyer: profile.first_time_buyer ?? null,
      co_buyer_name: profile.co_buyer_name ?? '',
    }));
  }, [profile]);

  useEffect(() => {
    if (!preferences) return;
    setFormData((prev) => ({
      ...prev,
      min_bedrooms: preferences.min_bedrooms ?? '',
      max_bedrooms: preferences.max_bedrooms ?? '',
      min_bathrooms: preferences.min_bathrooms ?? '',
      max_bathrooms: preferences.max_bathrooms ?? '',
      property_types: preferences.property_types ?? [],
      must_haves: preferences.must_haves ?? [],
      nice_to_haves: preferences.nice_to_haves ?? [],
      max_hoa_fee: preferences.max_hoa_fee ?? '',
      preferred_locations: preferences.preferred_locations ?? [],
    }));
  }, [preferences]);

  const setField = (name, value) => setFormData((p) => ({ ...p, [name]: value }));
  const onInput = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };
  const toggleInArray = (key, value) => {
    setFormData((p) => {
      const arr = p[key] || [];
      return {
        ...p,
        [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  };

  const addLocation = () => {
    const v = locationDraft.trim();
    if (!v) return;
    if (!formData.preferred_locations.includes(v)) {
      setField('preferred_locations', [...formData.preferred_locations, v]);
    }
    setLocationDraft('');
  };
  const removeLocation = (loc) => {
    setField('preferred_locations', formData.preferred_locations.filter((l) => l !== loc));
  };

  // "Bedrooms 4–2" used to save without a word (QA run 7 S16).
  const rangeErrors = useMemo(() => findRangeErrors({
    min_bedrooms: formData.min_bedrooms,
    max_bedrooms: formData.max_bedrooms,
    min_bathrooms: formData.min_bathrooms,
    max_bathrooms: formData.max_bathrooms,
  }), [formData.min_bedrooms, formData.max_bedrooms, formData.min_bathrooms, formData.max_bathrooms]);
  const rangeInvalid = hasRangeErrors(rangeErrors);

  const canAdvance = useMemo(() => {
    if (stepIdx === 0) {
      return Number(formData.budget_ceiling) > 0
        && Number(formData.timeline_months) >= 1;
    }
    if (stepIdx === PREFERENCES_STEP) return !rangeInvalid;
    return true;
  }, [stepIdx, formData.budget_ceiling, formData.timeline_months, rangeInvalid]);

  const next = () => {
    setSubmitError(null);
    if (!canAdvance) {
      setSubmitError(stepIdx === 0
        ? 'Budget and timeline are required to continue.'
        : 'Fix the highlighted ranges to continue.');
      return;
    }
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const back = () => {
    setSubmitError(null);
    setStepIdx((i) => Math.max(i - 1, 0));
  };

  const persist = async () => {
    setSubmitError(null);
    // The stepper lets the user jump straight to Review, past the inline
    // errors, so send them back to the preferences step.
    if (rangeInvalid) {
      setStepIdx(PREFERENCES_STEP);
      setSubmitError('Fix the highlighted ranges before saving.');
      return;
    }
    setSubmitting(true);
    try {
      const profilePayload = {
        budget_ceiling: Number(formData.budget_ceiling) || 0,
        pre_approval_amount: numOrNull(formData.pre_approval_amount),
        down_payment_percent: Number(formData.down_payment_percent) || 20,
        max_monthly_payment: numOrNull(formData.max_monthly_payment),
        timeline_months: Number(formData.timeline_months) || 6,
        financing_type: formData.financing_type || null,
        credit_score_range: formData.credit_score_range || null,
        employment_status: formData.employment_status || null,
        first_time_buyer: formData.first_time_buyer,
        co_buyer_name: formData.co_buyer_name || null,
      };
      if (profile) {
        await updateProfile(profilePayload);
      } else {
        await createProfile(profilePayload);
      }

      const prefPayload = {
        min_bedrooms: numOrNull(formData.min_bedrooms),
        max_bedrooms: numOrNull(formData.max_bedrooms),
        min_bathrooms: numOrNull(formData.min_bathrooms),
        max_bathrooms: numOrNull(formData.max_bathrooms),
        property_types: formData.property_types,
        must_haves: formData.must_haves,
        nice_to_haves: formData.nice_to_haves,
        preferred_locations: formData.preferred_locations,
        max_hoa_fee: numOrNull(formData.max_hoa_fee),
      };
      if (preferences) {
        await updatePreferences(prefPayload);
      } else {
        await createPreferences(prefPayload);
      }

      onComplete?.('Readiness saved successfully!');
    } catch (err) {
      setSubmitError(err?.message || 'Could not save your readiness profile.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="readiness-wizard">
      <div className="readiness-wizard__steps">
        <Stepper steps={STEPS} currentIndex={stepIdx} onStepClick={(_k, idx) => setStepIdx(idx)} />
      </div>

      <SectionCard>
        {stepIdx === 0 && (
          <div className="readiness-form">
            <h3 className="readiness-form__title">Your financial picture</h3>
            <p className="readiness-form__hint">
              Used to filter listings, score affordability, and pre-fill offers.
            </p>

            <div className="readiness-grid">
              <Field label="Budget ceiling" required suffix="$">
                <input type="number" name="budget_ceiling" min="0" step="1"
                  value={formData.budget_ceiling} onChange={onInput} placeholder="500000" />
              </Field>
              <Field label="Pre-approval amount" suffix="$">
                <input type="number" name="pre_approval_amount" min="0" step="1"
                  value={formData.pre_approval_amount} onChange={onInput} placeholder="480000" />
              </Field>
              {Number(formData.pre_approval_amount) > 0
                && Number(formData.budget_ceiling) > Number(formData.pre_approval_amount) && (
                <p className="readiness-hint" role="note" data-testid="budget-above-preapproval">
                  Your budget is above your pre-approval amount. A lender may not lend the difference.
                </p>
              )}
              <Field label="Down payment" suffix="%">
                <input type="number" name="down_payment_percent" min="0" max="100" step="0.5"
                  value={formData.down_payment_percent} onChange={onInput} />
              </Field>
              <Field label="Max monthly payment" suffix="$">
                <input type="number" name="max_monthly_payment" min="0" step="1"
                  value={formData.max_monthly_payment} onChange={onInput} placeholder="3000" />
              </Field>
              <Field label="Timeline" required suffix="months">
                <input type="number" name="timeline_months" min="1" max="120"
                  value={formData.timeline_months} onChange={onInput} />
              </Field>
              <Field label="Co-buyer name">
                <input type="text" name="co_buyer_name" maxLength={200}
                  value={formData.co_buyer_name} onChange={onInput} placeholder="Optional" />
              </Field>
            </div>

            <FieldGroup label="Financing type">
              <Segmented
                options={FINANCING_OPTIONS}
                value={formData.financing_type}
                onChange={(v) => setField('financing_type', v)}
              />
            </FieldGroup>

            <FieldGroup label="Credit score range">
              <Segmented
                options={CREDIT_OPTIONS}
                value={formData.credit_score_range}
                onChange={(v) => setField('credit_score_range', v)}
              />
            </FieldGroup>

            <FieldGroup label="Employment">
              <Segmented
                options={EMPLOYMENT_OPTIONS}
                value={formData.employment_status}
                onChange={(v) => setField('employment_status', v)}
              />
            </FieldGroup>

            <FieldGroup label="First-time buyer?">
              <Segmented
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]}
                value={formData.first_time_buyer === null ? '' : (formData.first_time_buyer ? 'yes' : 'no')}
                onChange={(v) => setField('first_time_buyer', v === 'yes')}
              />
            </FieldGroup>
          </div>
        )}

        {stepIdx === 1 && (
          <div className="readiness-form">
            <h3 className="readiness-form__title">What you're looking for</h3>
            <p className="readiness-form__hint">
              These drive search filters and recommended listings.
            </p>

            <div className="readiness-grid">
              <Field label="Min bedrooms">
                <input type="number" name="min_bedrooms" min="0" max="50"
                  value={formData.min_bedrooms} onChange={onInput} />
              </Field>
              <Field label="Max bedrooms" error={rangeErrors.max_bedrooms} errorId="readiness-max-bedrooms-error">
                <input type="number" name="max_bedrooms" min="0" max="50"
                  value={formData.max_bedrooms} onChange={onInput}
                  aria-invalid={rangeErrors.max_bedrooms ? true : undefined}
                  aria-describedby={rangeErrors.max_bedrooms ? 'readiness-max-bedrooms-error' : undefined} />
              </Field>
              <Field label="Min bathrooms">
                <input type="number" name="min_bathrooms" min="0" max="50" step="0.5"
                  value={formData.min_bathrooms} onChange={onInput} />
              </Field>
              <Field label="Max bathrooms" error={rangeErrors.max_bathrooms} errorId="readiness-max-bathrooms-error">
                <input type="number" name="max_bathrooms" min="0" max="50" step="0.5"
                  value={formData.max_bathrooms} onChange={onInput}
                  aria-invalid={rangeErrors.max_bathrooms ? true : undefined}
                  aria-describedby={rangeErrors.max_bathrooms ? 'readiness-max-bathrooms-error' : undefined} />
              </Field>
              <Field label="Max HOA fee" suffix="$/mo">
                <input type="number" name="max_hoa_fee" min="0" step="10"
                  value={formData.max_hoa_fee} onChange={onInput} placeholder="No limit" />
              </Field>
            </div>

            <FieldGroup label="Property types">
              <ChipGroup
                options={PROPERTY_TYPES}
                selected={formData.property_types}
                onToggle={(v) => toggleInArray('property_types', v)}
              />
            </FieldGroup>

            <FieldGroup label="Must-haves">
              <ChipGroup
                options={COMMON_MUST_HAVES}
                selected={formData.must_haves}
                onToggle={(v) => toggleInArray('must_haves', v)}
              />
            </FieldGroup>

            <FieldGroup label="Nice-to-haves">
              <ChipGroup
                options={COMMON_NICE_TO_HAVES}
                selected={formData.nice_to_haves}
                onToggle={(v) => toggleInArray('nice_to_haves', v)}
              />
            </FieldGroup>
          </div>
        )}

        {stepIdx === 2 && (
          <div className="readiness-form">
            <h3 className="readiness-form__title">Where you want to live</h3>
            <p className="readiness-form__hint">
              Add cities, neighborhoods, or zip codes. Used to scope listings & alerts.
            </p>

            <div className="chips-input">
              <input
                type="text"
                value={locationDraft}
                onChange={(e) => setLocationDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); addLocation(); }
                }}
                placeholder="e.g., Seattle, WA"
              />
              <button type="button" className="readiness-btn readiness-btn--ghost" onClick={addLocation}>
                Add
              </button>
            </div>

            {formData.preferred_locations.length === 0 && (
              <div className="readiness-empty">No locations yet — add one above.</div>
            )}
            <div className="chip-row">
              {formData.preferred_locations.map((loc) => (
                <span key={loc} className="chip chip--selected">
                  {loc}
                  <button type="button" className="chip__remove" onClick={() => removeLocation(loc)} aria-label={`Remove ${loc}`}>×</button>
                </span>
              ))}
            </div>
          </div>
        )}

        {stepIdx === 3 && (
          <div className="readiness-form">
            <h3 className="readiness-form__title">Review & save</h3>
            <p className="readiness-form__hint">Confirm before saving — you can edit anytime.</p>

            <ReviewBlock title="Financials" rows={[
              ['Budget ceiling', currency(formData.budget_ceiling)],
              ['Pre-approval', currency(formData.pre_approval_amount)],
              ['Down payment', formData.down_payment_percent ? `${formData.down_payment_percent}%` : '—'],
              ['Max monthly', currency(formData.max_monthly_payment)],
              ['Timeline', formData.timeline_months ? `${formData.timeline_months} months` : '—'],
              ['Financing', labelFor(FINANCING_OPTIONS, formData.financing_type)],
              ['Credit', labelFor(CREDIT_OPTIONS, formData.credit_score_range)],
              ['Employment', labelFor(EMPLOYMENT_OPTIONS, formData.employment_status)],
              ['First-time buyer', formData.first_time_buyer === null ? '—' : (formData.first_time_buyer ? 'Yes' : 'No')],
              ['Co-buyer', formData.co_buyer_name || '—'],
            ]} />

            <ReviewBlock title="Preferences" rows={[
              ['Bedrooms', range(formData.min_bedrooms, formData.max_bedrooms)],
              ['Bathrooms', range(formData.min_bathrooms, formData.max_bathrooms)],
              ['Property types', formData.property_types.join(', ') || '—'],
              ['Must-haves', formData.must_haves.join(', ') || '—'],
              ['Nice-to-haves', formData.nice_to_haves.join(', ') || '—'],
              ['Max HOA', formData.max_hoa_fee ? `$${formData.max_hoa_fee}/mo` : '—'],
            ]} />

            <ReviewBlock title="Locations" rows={[
              ['Cities / areas', formData.preferred_locations.join(', ') || '—'],
            ]} />
          </div>
        )}

        {submitError && (
          <div className="readiness-error" role="alert">{submitError}</div>
        )}

        <div className="readiness-actions">
          <div>
            {onCancel && (
              <button type="button" className="readiness-btn readiness-btn--ghost" onClick={onCancel}>
                Cancel
              </button>
            )}
          </div>
          <div className="readiness-actions__right">
            {stepIdx > 0 && (
              <button type="button" className="readiness-btn readiness-btn--ghost" onClick={back}>
                Back
              </button>
            )}
            {stepIdx < STEPS.length - 1 && (
              <button type="button" className="readiness-btn readiness-btn--primary" onClick={next} disabled={!canAdvance}>
                Continue
              </button>
            )}
            {stepIdx === STEPS.length - 1 && (
              <button type="button" className="readiness-btn readiness-btn--primary" onClick={persist} disabled={submitting}>
                {submitting ? 'Saving…' : 'Save readiness'}
              </button>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────

function Field({ label, required, suffix, error, errorId, children }) {
  return (
    <label className="field">
      <span className="field__label">
        {label}{required && <span className="field__required"> *</span>}
      </span>
      <span className={`field__control ${suffix ? 'field__control--has-suffix' : ''}`}>
        {children}
        {suffix && <span className="field__suffix">{suffix}</span>}
      </span>
      <FieldError id={errorId} message={error} />
    </label>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div className="field-group">
      <div className="field-group__label">{label}</div>
      {children}
    </div>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <div className="segmented" role="radiogroup">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          className={`segmented__btn ${value === opt.value ? 'segmented__btn--active' : ''}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ChipGroup({ options, selected, onToggle }) {
  return (
    <div className="chip-row">
      {options.map((opt) => {
        const isOn = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            className={`chip ${isOn ? 'chip--selected' : ''}`}
            onClick={() => onToggle(opt)}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function ReviewBlock({ title, rows }) {
  return (
    <div className="review-block">
      <h4 className="review-block__title">{title}</h4>
      <dl className="review-block__list">
        {rows.map(([k, v]) => (
          <div className="review-block__row" key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────
function currency(v) {
  if (v === '' || v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
function range(lo, hi) {
  const a = lo === '' || lo === null ? null : lo;
  const b = hi === '' || hi === null ? null : hi;
  if (a === null && b === null) return '—';
  if (a !== null && b !== null) return `${a}–${b}`;
  if (a !== null) return `${a}+`;
  return `≤ ${b}`;
}
function labelFor(options, value) {
  if (!value) return '—';
  return options.find((o) => o.value === value)?.label ?? value;
}

export default BuyerProfileWizard;
